import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BaseApi } from '../base-api';
import {
    Chat,
    ChatUser,
    ChatMessage,
    ChatListResponse,
    UserListResponse,
    MessageListResponse,
    UnreadCountResponse,
    SendMessageRequest,
    TypingRequest,
} from '@app/core/types/chat.types';

@Injectable({
    providedIn: 'root',
})
export class ChatService extends BaseApi {
    private readonly chatEndpoint = 'chat';

    /** Shared real-time unread count — updated by both chat-sidebar and main sidebar */
    private _globalUnreadCount = new BehaviorSubject<number>(0);
    readonly globalUnreadCount$ = this._globalUnreadCount.asObservable();

    setGlobalUnreadCount(count: number): void {
        this._globalUnreadCount.next(count);
    }

    /**
     * Get all chats for the authenticated user
     */
    getChats(page: number = 1, perPage: number = 20): Observable<ChatListResponse> {
        return this.get<ChatListResponse>(`${this.chatEndpoint}/chats`, {
            params: { page, per_page: perPage },
        });
    }

    /**
     * Get a single chat by ID
     */
    getChat(chatId: number): Observable<Chat> {
        return this.get<Chat>(`${this.chatEndpoint}/chats/${chatId}`);
    }

    /**
     * Get list of users for chatting
     */
    getUsers(page: number = 1, perPage: number = 20, search?: string): Observable<UserListResponse> {
        const params: any = { page, per_page: perPage };
        if (search) {
            params.search = search;
        }
        return this.get<UserListResponse>(`${this.chatEndpoint}/users`, { params });
    }

    /**
     * Get or create chat with a specific user
     */
    getOrCreateChat(userId: number): Observable<Chat> {
        return this.get<Chat>(`${this.chatEndpoint}/chats/${userId}/get-or-create`);
    }

    /**
     * Get messages for a specific chat
     */
    getMessages(
        chatId: number,
        page: number = 1,
        perPage: number = 50,
        search?: string,
    ): Observable<MessageListResponse> {
        const params: any = { page, per_page: perPage };
        if (search) {
            params.search = search;
        }
        return this.get<MessageListResponse>(`${this.chatEndpoint}/chats/${chatId}/messages`, { params });
    }

    /**
     * Send a message in a chat
     */
    sendMessage(chatId: number, message: string): Observable<ChatMessage> {
        const payload: SendMessageRequest = { message };
        return this.post<ChatMessage>(`${this.chatEndpoint}/chats/${chatId}/messages`, payload);
    }

    /**
     * Mark a message as delivered
     */
    markAsDelivered(chatId: number, messageId: number): Observable<{ message: string }> {
        return this.post<{ message: string }>(
            `${this.chatEndpoint}/chats/${chatId}/messages/${messageId}/delivered`,
            {},
        );
    }

    /**
     * Mark a message as read
     */
    markAsRead(chatId: number, messageId: number): Observable<{ message: string }> {
        return this.post<{ message: string }>(`${this.chatEndpoint}/chats/${chatId}/messages/${messageId}/read`, {});
    }

    /**
     * Mark all messages in a chat as read
     */
    markAllAsRead(chatId: number): Observable<{ message: string; count: number }> {
        return this.post<{ message: string; count: number }>(`${this.chatEndpoint}/chats/${chatId}/mark-all-read`, {});
    }

    /**
     * Send typing indicator
     */
    sendTyping(chatId: number, isTyping: boolean): Observable<{ message: string }> {
        const payload: TypingRequest = { is_typing: isTyping };
        return this.post<{ message: string }>(`${this.chatEndpoint}/chats/${chatId}/typing`, payload);
    }

    /**
     * Get unread message count
     */
    getUnreadCount(): Observable<UnreadCountResponse> {
        return this.get<UnreadCountResponse>(`${this.chatEndpoint}/unread-count`);
    }

    /**     * Get disclaimer write-up
     */
    getDisclaimerWriteUp(): Observable<{ data: string }> {
        return this.get<{ data: string }>(`${this.chatEndpoint}/getDisclaimerWriteUp`);
    }
}
